import { useEffect, useState } from 'react';

const stats = [
  {
    value: '10,000+',
    label: 'Apps Built',
    icon: 'ri-smartphone-line',
    color: 'from-purple-500 to-pink-500',
  },
  {
    value: '50+',
    label: 'Templates',
    icon: 'ri-layout-grid-line',
    color: 'from-pink-500 to-orange-500',
  },
  {
    value: '99.9%',
    label: 'Uptime',
    icon: 'ri-shield-check-line',
    color: 'from-green-500 to-teal-500',
  },
  {
    value: '24/7',
    label: 'Support',
    icon: 'ri-customer-service-line',
    color: 'from-blue-500 to-indigo-500',
  },
];

const reasons = [
  {
    title: 'No Coding Required',
    description: 'Build professional apps with our intuitive drag-and-drop interface. No programming knowledge needed.',
    icon: 'ri-code-s-slash-line',
  },
  {
    title: 'Cross-Platform',
    description: 'Create once, deploy everywhere. Your app works perfectly on both iOS and Android devices.',
    icon: 'ri-device-line',
  },
  {
    title: 'Lightning Fast',
    description: 'Go from idea to published app in hours, not months. Our streamlined process saves you time and money.',
    icon: 'ri-flashlight-line',
  },
  {
    title: 'Fully Customizable',
    description: 'Every element is customizable. Match your brand perfectly with unlimited design possibilities.',
    icon: 'ri-palette-line',
  },
  {
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security and 99.9% uptime guarantee. Your app and data are always protected.',
    icon: 'ri-lock-line',
  },
  {
    title: 'Expert Support',
    description: '24/7 customer support from our team of app development experts. We are here to help you succeed.',
    icon: 'ri-team-line',
  },
];

export default function WhyChooseUs() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('why-choose-us');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-choose-us" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-purple-100 rounded-full text-purple-700 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <i className="ri-star-line"></i>
            <span className="text-sm font-semibold">Why Your App Wizard</span>
          </div>
          <h2 className={`text-5xl font-extrabold text-gray-900 mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Why Choose Us
          </h2>
          <p className={`text-xl text-gray-600 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            The most powerful and easiest app builder trusted by thousands of creators worldwide
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-500 text-center group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                <i className={`${stat.icon} text-3xl text-white`}></i>
              </div>
              <div className="text-5xl font-extrabold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-gray-600 font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <div
              key={reason.title}
              className={`bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-500 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${500 + index * 100}ms` }}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className={`${reason.icon} text-3xl text-purple-600`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
              <p className="text-gray-600 leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}